import { useEffect, useRef, useState } from "react";
import { motion, useMotionValueEvent, useScroll, useTransform } from "framer-motion";
import { useLocation } from "react-router-dom";
import "@fontsource-variable/eb-garamond";
import "@fontsource-variable/eb-garamond/wght-italic.css";

const TEL = "tel:+13462170787";
const GOLD = "#D4AF37";
const setOffset = (px) => document.documentElement.style.setProperty("--whisper-offset", `${Math.max(0, Math.round(px))}px`);

// Warmth arrives slowly and leaves even more slowly: the light answers attention rather than snapping to it.
const arrive = { duration: 1.4, ease: [0.16, 1, 0.3, 1] };
const recede = { duration: 2.2, ease: [0.4, 0, 0.2, 1] };
const primary = {
  rest: { color: "rgba(255,255,255,0.78)", textShadow: "0 0 0px rgba(212,175,55,0)", letterSpacing: "0.02em", transition: recede },
  warm: { color: GOLD, textShadow: "0 0 22px rgba(212,175,55,0.38)", letterSpacing: "0.034em", transition: arrive },
};
const signature = {
  rest: { color: "rgba(255,255,255,0.42)", textShadow: "0 0 0px rgba(212,175,55,0)", transition: recede },
  warm: { color: "rgba(212,175,55,0.82)", textShadow: "0 0 16px rgba(212,175,55,0.28)", transition: arrive },
};
const rule = { rest: { opacity: 0.2, transition: recede }, warm: { opacity: 0.55, transition: arrive } };

// A razor-thin apparition above the navigation. It rides up with the first scroll and hands the top edge back to the header.
export default function WhisperBar() {
  const { pathname } = useLocation();
  const onLumen = pathname === "/lumen" || pathname.startsWith("/lumen/");
  const ref = useRef(null);
  const [h, setH] = useState(0);
  const [hidden, setHidden] = useState(false);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, (v) => -Math.min(v, h));
  const opacity = useTransform(scrollY, [0, Math.max(h * 0.85, 1)], [1, 0]);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      setH(0);
      return undefined;
    }
    const ro = new ResizeObserver(([entry]) => setH(entry.contentRect.height));
    ro.observe(el);
    return () => ro.disconnect();
  }, [onLumen]);

  useEffect(() => {
    setOffset(onLumen ? 0 : h - window.scrollY);
    return () => setOffset(0);
  }, [h, onLumen]);

  useMotionValueEvent(scrollY, "change", (v) => {
    if (!onLumen) setOffset(h - v);
    setHidden(v >= h * 0.85);
  });

  if (onLumen) return null;

  return (
    <motion.div ref={ref} data-testid="whisper-bar" style={{ y, opacity, pointerEvents: hidden ? "none" : "auto" }} className="whisper-bar fixed inset-x-0 top-0 z-[52]">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1.8, ease: "easeOut" }}
        className="relative bg-black/30 pt-[env(safe-area-inset-top)] backdrop-blur-md"
      >
        <motion.a
          href={TEL}
          data-testid="whisper-bar-link"
          tabIndex={hidden ? -1 : 0}
          aria-label="Call the founder, +1 346 217 0787"
          initial="rest"
          animate="rest"
          whileHover="warm"
          whileFocus="warm"
          className="font-whisper flex h-7 items-center justify-center gap-x-3 whitespace-nowrap px-4 text-center leading-none outline-none sm:h-8"
        >
          <motion.span variants={primary} data-testid="whisper-bar-primary" className="min-w-0 truncate text-[11.5px] sm:text-[13px]">
            <span className="sm:hidden">A direct line to the founder, +1.346.217.0787</span>
            <span className="hidden sm:inline">For visionary capital and architectural foresight: A direct line to the founder, +1.346.217.0787.</span>
          </motion.span>
          <motion.span variants={signature} aria-hidden="true" className="hidden h-px w-5 shrink-0 bg-current lg:block" />
          <motion.span variants={signature} data-testid="whisper-bar-signature" className="hidden text-[12px] italic lg:inline">
            Faithfully bringing the latent to light, — Charlie.
          </motion.span>
          <motion.span variants={rule} aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-[#D4AF37]" />
        </motion.a>
      </motion.div>
    </motion.div>
  );
}
